import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, PieChart, Wallet, ArrowLeft, Cpu, X, Compass, History, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const Niya = ({ t, isDark }) => {
    const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const htmlElement = document.documentElement;
        const bodyElement = document.body;

        if (isPrivacyOpen) {
            htmlElement.style.overflow = 'hidden';
            htmlElement.style.height = '100%';
            bodyElement.style.overflow = 'hidden';
            bodyElement.style.height = '100%';
            bodyElement.setAttribute('data-modal-open', 'true');
        } else {
            htmlElement.style.overflow = '';
            htmlElement.style.height = '';
            bodyElement.style.overflow = '';
            bodyElement.style.height = '';
            bodyElement.removeAttribute('data-modal-open');
        }

        return () => {
            htmlElement.style.overflow = '';
            htmlElement.style.height = '';
            bodyElement.style.overflow = '';
            bodyElement.style.height = '';
            bodyElement.removeAttribute('data-modal-open');
        };
    }, [isPrivacyOpen]);

    // Fallback defensivo por si t.niya no se ha cargado
    const n = t?.niya || {};

    return (
        <div className={`min-h-screen pt-32 pb-24 px-6 transition-colors duration-700
      ${isDark ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900'}`}>

            <style dangerouslySetInnerHTML={{__html: `
                .no-scrollbar::-webkit-scrollbar {
                    display: none !important;
                    width: 0 !important;
                    height: 0 !important;
                }
                .no-scrollbar {
                    -ms-overflow-style: none !important;
                    scrollbar-width: none !important;
                }
                
                body[data-modal-open="true"] .nav-container {
                    z-index: 0 !important;
                    pointer-events: none !important;
                    opacity: 0 !important;
                    transition: opacity 0.3s ease;
                }
            `}} />

            <div className="container mx-auto max-w-5xl relative z-10">

                <Link to="/" className={`inline-flex items-center gap-2 mb-12 font-black uppercase text-[10px] tracking-widest transition-all hover:-translate-x-1
          ${isDark ? 'text-slate-400' : 'text-slate-600 hover:text-blue-600'}`}>
                    <ArrowLeft size={16} /> {n.back_home || "Volver al Inicio"}
                </Link>

                {/* HERO SECTION */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center mb-16">
                    <div className="md:col-span-7 text-left">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-ping" />
                            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-emerald-500">
                                {n.badge}
                            </span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tighter italic mb-6">
                            {n.hero_title || "Niya"}
                        </h1>
                        <p className="text-xl font-bold leading-relaxed opacity-80 mb-8">
                            {n.hero_subtitle}
                        </p>

                        <button
                            onClick={() => setIsPrivacyOpen(true)}
                            className="px-8 py-4 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500 hover:text-white text-emerald-500 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center gap-3 transition-all duration-300 shadow-lg shadow-emerald-500/5 cursor-pointer"
                        >
                            <Shield size={16} /> {n.privacy_btn}
                        </button>
                    </div>

                    <div className="md:col-span-5 flex justify-center">
                        <div className={`w-64 h-64 rounded-[3rem] border backdrop-blur-3xl flex items-center justify-center relative shadow-2xl
              ${isDark ? 'bg-slate-900/60 border-white/10 shadow-black/50' : 'bg-white border-slate-200 shadow-emerald-500/10'}`}>
                            
                            <div className="w-32 h-32 rounded-full overflow-hidden flex items-center justify-center bg-emerald-500/5 border border-emerald-500/20 p-2">
                                <img 
                                    src="/logo_niya.png" 
                                    alt="Niya App Logo" 
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            
                            <div className="absolute -inset-4 rounded-[3.5rem] border border-emerald-500/20 animate-spin" style={{ animationDuration: '15s' }} />
                        </div>
                    </div>
                </div>

                {/* PILARES DE IDENTIDAD DE MARCA */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 text-left">
                    <div className={`p-8 md:p-10 rounded-[3.5rem] border backdrop-blur-3xl
                        ${isDark ? 'bg-slate-900/40 border-white/10' : 'bg-white border-slate-200 shadow-lg shadow-emerald-500/5'}`}>
                        <span className="text-[9px] font-black uppercase tracking-widest text-emerald-500 block mb-2">{n.brand_pillars?.meaning?.tag}</span>
                        <h2 className="text-2xl font-black uppercase tracking-tight mb-4">{n.brand_pillars?.meaning?.title}</h2>
                        <p className="text-sm font-bold opacity-60 leading-relaxed">
                            {n.brand_pillars?.meaning?.desc_p1} <strong>{n.brand_pillars?.meaning?.desc_highlight}</strong>{n.brand_pillars?.meaning?.desc_p2}
                        </p>
                    </div>

                    <div className={`p-8 md:p-10 rounded-[3.5rem] border backdrop-blur-3xl
                        ${isDark ? 'bg-slate-900/40 border-white/10' : 'bg-white border-slate-200 shadow-lg shadow-emerald-500/5'}`}>
                        <span className="text-[9px] font-black uppercase tracking-widest text-blue-500 block mb-2">{n.brand_pillars?.evolution?.tag}</span>
                        <h2 className="text-2xl font-black uppercase tracking-tight mb-4">{n.brand_pillars?.evolution?.title}</h2>
                        <p className="text-sm font-bold opacity-60 leading-relaxed">
                            {n.brand_pillars?.evolution?.desc}
                        </p>
                    </div>
                </div>

                {/* DESAFÍO Y SOLUCIÓN GENERAL */}
                <div className={`p-8 md:p-12 rounded-[3.5rem] border backdrop-blur-3xl mb-12 text-left
          ${isDark ? 'bg-slate-900/40 border-white/10' : 'bg-white border-slate-200 shadow-lg shadow-emerald-500/5'}`}>
                    <span className="text-[9px] font-black uppercase tracking-widest text-emerald-500 block mb-2">{n.vision?.tag}</span>
                    <h2 className="text-2xl font-black uppercase tracking-tight mb-6">{n.vision?.title}</h2>
                    <p className="text-base font-bold opacity-70 leading-relaxed">
                        {n.description}
                    </p>
                </div>

                {/* BENTO GRID: TRAYECTORIA EN EL TIEMPO */}
                <h3 className="text-xs font-black uppercase tracking-[0.4em] text-emerald-500 text-left mb-6 pl-4">{n.pathway?.section_title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 text-left mb-12">
                    <div className={`md:col-span-4 p-8 rounded-[2.5rem] border ${isDark ? 'bg-slate-900/20 border-white/5' : 'bg-white border-slate-100 shadow-sm'}`}>
                        <div className="p-3 bg-blue-500/10 rounded-xl text-blue-500 w-fit mb-4">
                            <History size={20} />
                        </div>
                        <h4 className="text-lg font-black mb-2">{n.pathway?.past?.title}</h4>
                        <p className="text-xs font-bold opacity-50 leading-relaxed">{n.pathway?.past?.desc}</p>
                    </div>

                    <div className={`md:col-span-4 p-8 rounded-[2.5rem] border ${isDark ? 'bg-slate-900/20 border-white/5' : 'bg-white border-slate-100 shadow-sm'}`}>
                        <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-500 w-fit mb-4">
                            <Wallet size={20} />
                        </div>
                        <h4 className="text-lg font-black mb-2">{n.pathway?.present?.title}</h4>
                        <p className="text-xs font-bold opacity-50 leading-relaxed">{n.pathway?.present?.desc}</p>
                    </div>

                    <div className={`md:col-span-4 p-8 rounded-[2.5rem] border ${isDark ? 'bg-slate-900/20 border-white/5' : 'bg-white border-slate-100 shadow-sm'}`}>
                        <div className="p-3 bg-purple-500/10 rounded-xl text-purple-500 w-fit mb-4">
                            <Compass size={20} />
                        </div>
                        <h4 className="text-lg font-black mb-2">{n.pathway?.future?.title}</h4>
                        <p className="text-xs font-bold opacity-50 leading-relaxed">{n.pathway?.future?.desc}</p>
                    </div>
                </div>

                {/* INFRAESTRUCTURA TÉCNICA */}
                <h4 className="text-xs font-black uppercase tracking-[0.4em] text-emerald-500 text-left mb-6 pl-4">{n.engineering?.section_title}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                    {[
                        { icon: <PieChart size={24} />, title: n.engineering?.item1?.title, desc: n.engineering?.item1?.desc },
                        { icon: <Cpu size={24} />, title: n.engineering?.item2?.title, desc: n.engineering?.item2?.desc },
                        { icon: <Sparkles size={24} />, title: n.engineering?.item3?.title, desc: n.engineering?.item3?.desc }
                    ].map((item, idx) => (
                        <div key={idx} className={`p-8 rounded-[2.5rem] border transition-all duration-300
              ${isDark ? 'bg-slate-900/30 border-white/5 hover:border-emerald-500/20' : 'bg-white border-slate-200 hover:border-emerald-500/20 shadow-md shadow-emerald-500/5'}`}>
                            <div className="text-emerald-500 mb-4">{item.icon}</div>
                            <h3 className="text-lg font-black mb-2">{item.title}</h3>
                            <p className="text-xs font-bold opacity-50 leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>

            </div>

            {/* PRIVACY POLICY MODAL */}
            <AnimatePresence>
                {isPrivacyOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 md:p-6 bg-slate-950/95 backdrop-blur-3xl"
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            className={`relative w-full max-w-2xl p-6 sm:p-8 md:p-12 rounded-[2.5rem] md:rounded-[3rem] border shadow-2xl text-left flex flex-col max-h-[80vh]
                ${isDark ? 'bg-[#0E1526] border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                        >
                            <button
                                onClick={() => setIsPrivacyOpen(false)}
                                className={`absolute top-4 right-4 md:top-6 md:right-6 p-2 rounded-full border transition-colors z-[100]
                  ${isDark ? 'border-white/10 hover:bg-white/10 bg-[#0E1526]' : 'border-slate-200 hover:bg-slate-100 bg-white'}`}
                            >
                                <X size={18} />
                            </button>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 pr-10">
                                <div className="w-12 h-12 rounded-2xl overflow-hidden border border-emerald-500/20 flex items-center justify-center bg-emerald-500/10 shrink-0 p-2">
                                    <img 
                                        src="/logo192.png" 
                                        alt="Logo Niya" 
                                        className="w-full h-full object-contain" 
                                    />
                                </div>
                                <div className="space-y-1">
                                    <h2 className="text-xl md:text-2xl font-black tracking-tight text-emerald-500 leading-tight">
                                        {n.privacy?.title}
                                    </h2>
                                    <p className="text-[8px] md:text-[9px] font-black uppercase tracking-wider opacity-40">
                                        {n.privacy?.badge}
                                    </p>
                                </div>
                            </div>

                            <div className="h-px bg-current opacity-10 mb-6 shrink-0" />
                            <div className="space-y-6 text-xs md:text-sm font-bold opacity-70 leading-relaxed overflow-y-auto pr-2 flex-grow no-scrollbar">
                                <h3 className="text-sm md:text-base font-black opacity-100 text-slate-500 dark:text-slate-400">
                                    {n.privacy?.subtitle}
                                </h3>
                                <p>{n.privacy?.p1}</p>
                                <p>{n.privacy?.p2}</p>
                                <p>{n.privacy?.p3}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default Niya;